import { useState, useEffect, useRef } from "react";
import { Layout } from "@/components/Layout";
import { ChatCircleText, Copy, CheckCircle, Code, Globe, PaperPlaneTilt, X, User, Robot, Headset } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to render text with clickable links
const renderMessageContent = (content) => {
  if (!content) return null;
  
  // Regex to match URLs and markdown links
  const urlRegex = /(https?:\/\/[^\s<>)"]+)/g;
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  
  // First, replace markdown links
  let processedContent = content.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-indigo-600 underline hover:text-indigo-800">$1</a>');
  
  // Then replace remaining plain URLs (that aren't already in href)
  processedContent = processedContent.replace(/(?<!href=")(https?:\/\/[^\s<>)"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-indigo-600 underline hover:text-indigo-800">$1</a>');
  
  // Handle **bold** markdown
  processedContent = processedContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Handle line breaks
  processedContent = processedContent.replace(/\n/g, '<br/>');
  
  return <span dangerouslySetInnerHTML={{ __html: processedContent }} />;
};

const ChatWidget = ({ config, isOpen, onToggle }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [handoffSent, setHandoffSent] = useState(false);
  const [showHandoffForm, setShowHandoffForm] = useState(false);
  const [handoffData, setHandoffData] = useState({ email: "", phone: "", name: "" });
  const [sessionId] = useState(() => `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, showHandoffForm]);

  useEffect(() => {
    if (config?.welcome_message && messages.length === 0) {
      setMessages([{ role: 'assistant', content: config.welcome_message, timestamp: new Date().toISOString() }]);
    }
  }, [config]);

  const sendMessage = async () => {
    if (!input.trim() || loading || !config?.site_key) return;
    const userMessage = { role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    try {
      const response = await fetch(`${API}/chat/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_key: config.site_key, session_id: sessionId, page_url: window.location.href, user_message: userMessage.content })
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.answer, timestamp: new Date().toISOString(), handoff_needed: data.handoff_needed }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: "Beklager, der opstod en fejl. Prøv igen.", timestamp: new Date().toISOString() }]);
      }
    } catch (error) { setMessages(prev => [...prev, { role: 'assistant', content: "Der opstod en fejl.", timestamp: new Date().toISOString() }]); }
    finally { setLoading(false); }
  };

  const initiateHandoff = () => {
    // Show the contact form instead of immediately sending handoff
    setShowHandoffForm(true);
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: "For at vi kan kontakte dig, har jeg brug for dine kontaktoplysninger:", 
      timestamp: new Date().toISOString(),
      isHandoffPrompt: true
    }]);
  };

  const submitHandoff = async () => {
    if (!handoffData.email || !handoffData.phone) {
      return; // Validation handled by required fields
    }
    
    if (handoffSent || !config?.site_key) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/chat/handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_key: config.site_key,
          session_id: sessionId,
          page_url: window.location.href,
          customer_email: handoffData.email,
          customer_name: handoffData.name || undefined,
          issue_summary: `Telefon: ${handoffData.phone} | ${messages.filter(m => m.role === 'user').map(m => m.content).join(' | ')}`
        })
      });
      
      if (response.ok) {
        setHandoffSent(true);
        setShowHandoffForm(false);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Tak for din henvendelse, ${handoffData.name || 'du'}! 🙏\n\nJeg har sendt din samtale videre til vores kundeservice, som vil kontakte dig hurtigst muligt på:\n\n📧 ${handoffData.email}\n📞 ${handoffData.phone}\n\nDu kan forvente svar inden for 1-2 hverdage.\n\nHav en god dag!`, 
          timestamp: new Date().toISOString() 
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "Beklager, der opstod en fejl. Prøv venligst igen eller kontakt os direkte.", 
          timestamp: new Date().toISOString() 
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Beklager, der opstod en fejl ved afsendelse. Prøv igen senere.", 
        timestamp: new Date().toISOString() 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  // Get widget colors from config with defaults
  const headerColor = config?.widget_header_color || "#4338CA";
  const bubbleColor = config?.widget_bubble_color || "#4F46E5";
  const userMessageColor = config?.widget_user_message_color || "#4F46E5";
  const widgetPosition = config?.widget_position || "right";

  if (!isOpen) {
    return (
      <motion.button 
        initial={{ scale: 0 }} 
        animate={{ scale: 1 }} 
        whileHover={{ scale: 1.1 }} 
        whileTap={{ scale: 0.95 }} 
        onClick={onToggle} 
        className="chat-bubble"
        style={{ backgroundColor: bubbleColor, [widgetPosition]: '24px' }}
        data-testid="chat-bubble"
      >
        <ChatCircleText className="w-7 h-7 text-white" weight="fill" />
      </motion.button>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20, scale: 0.95 }} 
      animate={{ opacity: 1, y: 0, scale: 1 }} 
      exit={{ opacity: 0, y: 20, scale: 0.95 }} 
      className="chat-window"
      style={{ [widgetPosition]: '24px' }}
      data-testid="chat-window"
    >
      <div className="text-white p-4 flex items-center justify-between" style={{ backgroundColor: headerColor }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Robot className="w-5 h-5" weight="fill" /></div>
          <div><h3 className="font-semibold">{config?.brand_name || 'Kundeservice'}</h3><p className="text-xs opacity-80">Online nu</p></div>
        </div>
        <button onClick={onToggle} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="close-chat"><X className="w-5 h-5" /></button>
      </div>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex items-end gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div 
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: msg.role === 'user' ? `${userMessageColor}20` : '#f1f5f9' }}
                >
                  {msg.role === 'user' ? <User className="w-4 h-4" style={{ color: userMessageColor }} weight="fill" /> : <Robot className="w-4 h-4 text-slate-600" weight="fill" />}
                </div>
                <div 
                  className={`rounded-2xl px-4 py-2.5 ${msg.role === 'user' ? 'text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm'}`}
                  style={msg.role === 'user' ? { backgroundColor: userMessageColor } : {}}
                >
                  <div className="text-sm leading-relaxed">{renderMessageContent(msg.content)}</div>
                  {msg.handoff_needed && !handoffSent && !showHandoffForm && (
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      className="mt-2 w-full" 
                      onClick={initiateHandoff}
                      disabled={loading}
                      data-testid="handoff-btn"
                    >
                      <Headset className="w-4 h-4 mr-1" />
                      Kontakt kundeservice
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
          
          {/* Handoff Contact Form */}
          {showHandoffForm && !handoffSent && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
              <div className="flex items-end gap-2 max-w-[85%]">
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Robot className="w-4 h-4 text-slate-600" weight="fill" />
                </div>
                <div className="bg-slate-100 text-slate-700 rounded-2xl rounded-bl-sm px-4 py-3 w-full">
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Navn (valgfrit)</label>
                      <Input
                        type="text"
                        placeholder="Dit navn"
                        value={handoffData.name}
                        onChange={(e) => setHandoffData(prev => ({ ...prev, name: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid="handoff-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">E-mail *</label>
                      <Input
                        type="email"
                        placeholder="din@email.dk"
                        value={handoffData.email}
                        onChange={(e) => setHandoffData(prev => ({ ...prev, email: e.target.value }))}
                        className="h-9 text-sm"
                        required
                        data-testid="handoff-email"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Telefon *</label>
                      <Input
                        type="tel"
                        placeholder="12 34 56 78"
                        value={handoffData.phone}
                        onChange={(e) => setHandoffData(prev => ({ ...prev, phone: e.target.value }))}
                        className="h-9 text-sm"
                        required
                        data-testid="handoff-phone"
                      />
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full text-white"
                      style={{ backgroundColor: userMessageColor }}
                      onClick={submitHandoff}
                      disabled={loading || !handoffData.email || !handoffData.phone}
                      data-testid="submit-handoff-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sender...
                        </span>
                      ) : (
                        <>
                          <Headset className="w-4 h-4 mr-1" />
                          Send til kundeservice
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          {loading && !showHandoffForm && <div className="flex justify-start"><div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center"><Robot className="w-4 h-4 text-slate-600" weight="fill" /></div><div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3"><div className="flex gap-1"><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} /><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></div></div></div></div>}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      {messages.length === 1 && <div className="px-4 pb-2"><div className="flex gap-2 overflow-x-auto py-2">{['Leveringstid?', 'Returnering', 'Kontakt'].map(action => <button key={action} onClick={() => setInput(action)} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-full whitespace-nowrap transition-colors">{action}</button>)}</div></div>}
      <div className="p-4 border-t border-slate-100">
        <div className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress} placeholder="Skriv en besked..." className="flex-1" disabled={loading || showHandoffForm} data-testid="chat-input" />
          <Button 
            onClick={sendMessage} 
            disabled={loading || !input.trim() || showHandoffForm} 
            className="px-3 text-white"
            style={{ backgroundColor: userMessageColor }}
            data-testid="send-message-btn"
          >
            <PaperPlaneTilt className="w-5 h-5" weight="fill" />
          </Button>
        </div>
        <p className="text-xs text-slate-400 text-center mt-2">Drevet af AI</p>
      </div>
    </motion.div>
  );
};

export const WidgetPreviewPage = ({ user, setUser }) => {
  const [sites, setSites] = useState([]);
  const [selectedSiteKey, setSelectedSiteKey] = useState("");
  const [config, setConfig] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchSites(); }, []);
  useEffect(() => { if (selectedSiteKey) fetchConfig(selectedSiteKey); }, [selectedSiteKey]);

  const fetchSites = async () => {
    try { const response = await fetch(`${API}/sites`, { credentials: 'include' }); if (response.ok) { const data = await response.json(); setSites(data); if (data.length > 0) setSelectedSiteKey(data[0].site_key); } }
    catch (error) { console.error('Failed to fetch sites:', error); }
  };

  const fetchConfig = async (siteKey) => {
    try { const response = await fetch(`${API}/chat/config?site=${siteKey}`); if (response.ok) setConfig(await response.json()); }
    catch (error) { console.error('Failed to fetch config:', error); }
  };

  const embedCode = `<!-- GardinBot Chat Widget -->\n<script>\n  (function() {\n    var script = document.createElement('script');\n    script.src = '${window.location.origin}/widget.js';\n    script.async = true;\n    script.dataset.siteKey = '${selectedSiteKey}';\n    script.dataset.apiUrl = '${process.env.REACT_APP_BACKEND_URL}';\n    document.body.appendChild(script);\n  })();\n</script>`;

  const copyToClipboard = () => { navigator.clipboard.writeText(embedCode); setCopied(true); toast.success("Kode kopieret"); setTimeout(() => setCopied(false), 2000); };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="widget-preview-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Chat Widget</h1>
            <p className="text-slate-600">Preview og få embed kode</p>
          </div>
          <Select value={selectedSiteKey} onValueChange={setSelectedSiteKey}>
            <SelectTrigger className="w-[200px]" data-testid="select-site"><SelectValue placeholder="Vælg site" /></SelectTrigger>
            <SelectContent>{sites.map(site => <SelectItem key={site.site_key} value={site.site_key}>{site.brand_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {sites.length === 0 ? (
          <Card className="card-default"><CardContent className="p-12 text-center"><Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" weight="duotone" /><h3 className="text-lg font-semibold text-slate-900 mb-2">Ingen sites</h3><p className="text-slate-600">Opret først et site</p></CardContent></Card>
        ) : (
          <Tabs defaultValue="preview" className="space-y-6">
            <TabsList><TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger><TabsTrigger value="embed" data-testid="tab-embed">Embed kode</TabsTrigger></TabsList>
            <TabsContent value="preview">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="card-default">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><ChatCircleText className="w-5 h-5 text-indigo-600" weight="duotone" />Widget Preview</CardTitle></CardHeader>
                  <CardContent>
                    <div className="relative bg-slate-100 rounded-lg h-[500px] overflow-hidden">
                      <div className="absolute inset-0 p-4">
                        <div className="h-12 bg-white rounded mb-4 flex items-center px-4"><div className="w-24 h-6 bg-slate-200 rounded" /></div>
                        <div className="grid grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <div key={i} className="bg-white rounded-lg p-3"><div className="w-full h-20 bg-slate-200 rounded mb-2" /><div className="h-3 bg-slate-200 rounded w-3/4 mb-1" /><div className="h-3 bg-slate-200 rounded w-1/2" /></div>)}</div>
                      </div>
                      <div className="absolute bottom-4 right-4"><AnimatePresence><ChatWidget config={config} isOpen={chatOpen} onToggle={() => setChatOpen(!chatOpen)} /></AnimatePresence></div>
                    </div>
                  </CardContent>
                </Card>
                <div className="space-y-6">
                  <Card className="card-default">
                    <CardHeader><CardTitle className="text-base">Konfiguration</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {config && <>
                        <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Brand</span><span className="font-medium text-slate-900">{config.brand_name}</span></div>
                        <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Site key</span><code className="bg-slate-100 px-2 py-0.5 rounded text-sm">{config.site_key}</code></div>
                        <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Tone</span><span className="text-slate-700">{config.tone_of_voice}</span></div>
                        <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Handoff</span><span className={config.handoff_enabled ? "text-emerald-600" : "text-slate-400"}>{config.handoff_enabled ? "Aktiveret" : "Deaktiveret"}</span></div>
                        <div className="py-2"><span className="text-slate-500 block mb-1">Velkomstbesked</span><p className="text-slate-700 text-sm bg-slate-50 p-2 rounded">{config.welcome_message}</p></div>
                      </>}
                    </CardContent>
                  </Card>
                  <Card className="card-default border-indigo-200 bg-indigo-50/30"><CardContent className="p-4"><div className="flex items-start gap-3"><ChatCircleText className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" weight="duotone" /><p className="text-sm text-slate-700"><strong>Tip:</strong> Klik på chat-boblen for at teste widgetten.</p></div></CardContent></Card>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="embed">
              <Card className="card-default">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Code className="w-5 h-5 text-indigo-600" weight="duotone" />Embed kode</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">Kopier koden og indsæt før <code className="bg-slate-100 px-1 rounded">&lt;/body&gt;</code> tagget.</p>
                  <div className="relative">
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">{embedCode}</pre>
                    <Button onClick={copyToClipboard} className="absolute top-3 right-3" size="sm" variant="secondary" data-testid="copy-embed-code">{copied ? <><CheckCircle className="w-4 h-4 mr-1 text-emerald-600" />Kopieret</> : <><Copy className="w-4 h-4 mr-1" />Kopier</>}</Button>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4"><p className="text-sm text-amber-800"><strong>Bemærk:</strong> Embed koden er et eksempel. I produktion ville widget.js deployes til CDN.</p></div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
};
